import { Box, Grid } from '@mui/material';
import { useQuery } from 'react-query';
import { FundMovementsExperience } from 'src/components/FundMovements';
import { PanelLogin } from 'src/components/PanelLogin';
import SubTitle from 'src/components/v2/SubTitle';
import { useAuthContext } from 'src/context/AuthContextProvider';
import {
  FicFidcDashboardDateProvider,
  useFicFidcDashboardDate,
} from 'src/context/FicFidcDashboardDateContext';
import {
  getIsFic,
  getMetaCarteirasFicFidcsChildren,
  getMetaCarteirasFicFidcsWalletHistory,
} from 'src/services/meta-carteiras-fic-fidcs';

import FicFidcCompositionCharts from './FicFidcCompositionCharts';
import FicFidcConcentrationChart from './FicFidcConcentrationChart';
import FicFidcPositionsTable from './FicFidcPositionsTable';
import FicFidcWalletHistoryChart from './FicFidcWalletHistoryChart';

interface OperationFicFidcWalletProps {
  operationId: string;
}

export default function OperationFicFidcWallet({
  operationId,
}: OperationFicFidcWalletProps) {
  return (
    // key={operationId}: a rota reutiliza o componente ao trocar de operação;
    // remontar o provider reinicializa a competência e evita que a data da
    // operação anterior vaze para as queries da próxima.
    <FicFidcDashboardDateProvider key={operationId}>
      <OperationFicFidcWalletContent operationId={operationId} />
    </FicFidcDashboardDateProvider>
  );
}

export function OperationFicFidcWalletContent({
  operationId,
}: OperationFicFidcWalletProps) {
  const { auth } = useAuthContext();
  const dashboardDate = useFicFidcDashboardDate();
  const date = dashboardDate?.date ?? null;

  const isBlocked =
    !auth.isLoggedIn || auth?.user?.subscription?.plan?.type === 'free';

  const hasPermission =
    !!auth?.user?.subscription?.plan?.permissions
      ?.operacoes_fidic_ativo_carteira;

  // A competência selecionada é autoritativa também para o status is-fic:
  // sem date, o status atual decide (legado); com date, o status na
  // competência histórica decide e habilita/bloqueia as queries históricas.
  const isFicQuery = useQuery(
    date
      ? ['operation-is-fic', operationId, date]
      : ['operation-is-fic', operationId],
    () => (date ? getIsFic(operationId, { date }) : getIsFic(operationId)),
    {
      enabled: !!operationId && !isBlocked && hasPermission,
      retry: false,
      useErrorBoundary: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      refetchOnMount: 'always',
      cacheTime: 0,
      retryOnMount: false,
    },
  );

  const isFic = isFicQuery.data?.isFic === true;

  const { data, isLoading, isError } = useQuery(
    date
      ? ['operation-fic-fidc-children', operationId, date]
      : ['operation-fic-fidc-children', operationId],
    () =>
      date
        ? getMetaCarteirasFicFidcsChildren(operationId, { date })
        : getMetaCarteirasFicFidcsChildren(operationId),
    {
      enabled: !!operationId && !isBlocked && hasPermission && isFic,
      retry: false,
      useErrorBoundary: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      refetchOnMount: 'always',
      cacheTime: 0,
      retryOnMount: false,
    },
  );

  const {
    data: walletHistoryData,
    isLoading: isWalletHistoryLoading,
    isError: isWalletHistoryError,
  } = useQuery(
    date
      ? ['operation-fic-fidc-wallet-history', operationId, date]
      : ['operation-fic-fidc-wallet-history', operationId],
    () =>
      date
        ? getMetaCarteirasFicFidcsWalletHistory(operationId, { date })
        : getMetaCarteirasFicFidcsWalletHistory(operationId),
    {
      enabled: !!operationId && !isBlocked && hasPermission && isFic,
      retry: false,
      useErrorBoundary: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      refetchOnMount: 'always',
      cacheTime: 0,
      retryOnMount: false,
    },
  );

  const positions = data?.positions?.data ?? [];

  if (isBlocked) {
    return (
      <Box mt={4}>
        <SubTitle title="— Composição da Carteira" />
        <PanelLogin small={undefined} message={undefined} />
      </Box>
    );
  }

  if (hasPermission && (!isFicQuery.isSuccess || !isFic)) {
    return null;
  }

  return (
    <Box mt={4}>
      <Grid container>
        {hasPermission && isFic && (
          <Grid item xs={12}>
            <FicFidcWalletHistoryChart
              history={walletHistoryData?.data ?? []}
              isLoading={isWalletHistoryLoading}
              isError={isWalletHistoryError}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <FicFidcPositionsTable
            positions={positions}
            meta={data?.positions?.meta}
            isLoading={isLoading}
            hasPermission={hasPermission}
          />
          <FicFidcCompositionCharts
            baseMonth={
              data?.positions?.meta?.baseMonth ?? positions[0]?.dtComptc
            }
            hasPermission={hasPermission}
            positions={positions}
          />
          {hasPermission && isFic && (
            <FicFidcConcentrationChart
              isError={isError}
              isLoading={isLoading}
              meta={data?.positions?.meta}
              positions={positions}
            />
          )}
        </Grid>
        <Grid item xs={12}>
          <FundMovementsExperience
            operationId={operationId}
            childrenData={data}
            isLoadingChildren={isLoading}
            isErrorChildren={isError}
            hasPermission={hasPermission}
            title="Movimentação da carteira"
          />
        </Grid>
      </Grid>
    </Box>
  );
}
